import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global upload orchestration for Group→Portrait tasks.
 *
 * Uploads survive navigation within the SPA because this provider is
 * mounted at App root — the state, in-flight `fetch`es, and retry
 * timers are all in one place, unaffected by which page is currently
 * showing.
 *
 * Each task's files are PUT directly to R2 via presigned URLs. Per
 * file we retry up to MAX_RETRIES with exponential backoff on
 * network errors / 5xx responses.
 */

const MAX_RETRIES = 4;                // per-file attempts
const RETRY_BASE_MS = 800;            // 0.8 s → 1.6 → 3.2 → 6.4
const PROGRESS_SAMPLE_MS = 1200;      // don't ping the server more often than this

const UploadCtx = createContext(null);

async function retryingPut(url, blob, contentType, signal) {
  let lastErr;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (signal?.aborted) throw new Error("aborted");
    try {
      const res = await fetch(url, {
        method: "PUT",
        headers: contentType ? { "Content-Type": contentType } : {},
        body: blob,
        signal,
      });
      if (res.ok) return true;
      // 5xx → retry; 4xx → give up (probably a bad URL / expired presign)
      if (res.status < 500 && res.status !== 408 && res.status !== 429) {
        lastErr = new Error(`R2 PUT ${res.status}`);
        break;
      }
      lastErr = new Error(`R2 PUT ${res.status}`);
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      lastErr = err;
    }
    await new Promise((r) => setTimeout(r, RETRY_BASE_MS * 2 ** attempt));
  }
  throw lastErr || new Error("PUT failed after retries");
}

export function GroupPortraitUploadProvider({ children }) {
  // in-progress uploads keyed by task_id → {name, done, total, failed}
  const [uploads, setUploads] = useState({});
  const uploadsRef = useRef({});
  uploadsRef.current = uploads;

  // Cancellation flags — a task_id lands here the moment the user
  // hits "Delete" on an in-flight upload. The PUT worker loop
  // reads this on every iteration and bails out silently, and
  // `enqueueTask` skips /finalize.
  const cancelledRef = useRef(new Set());
  // task_id → AbortController for the in-flight PUT fetches so
  // cancelling really *does* stop a 20-MB upload mid-body rather than
  // waiting for it to finish streaming.
  const abortersRef = useRef(new Map());
  // Sequential task queue. Uploads for a second/third task must wait
  // for the first to finish uploading so we don't saturate the user's
  // outbound bandwidth (or Cloudflare's) with parallel PUT storms.
  const chainRef = useRef(Promise.resolve());

  // Warn on browser tab close if anything is still uploading.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      const active = Object.values(uploadsRef.current).some(
        (u) => u.status === "uploading",
      );
      if (active) {
        e.preventDefault();
        e.returnValue = "Uploads are still in progress. Leave anyway?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const updateUpload = (task_id, patch) => {
    setUploads((prev) => {
      const cur = prev[task_id] || {};
      const next = { ...prev, [task_id]: { ...cur, ...patch } };
      uploadsRef.current = next;
      return next;
    });
  };

  const removeUpload = (task_id) => {
    setUploads((prev) => {
      const next = { ...prev };
      delete next[task_id];
      uploadsRef.current = next;
      return next;
    });
  };

  const cancelUpload = (task_id) => {
    cancelledRef.current.add(task_id);
    const ctl = abortersRef.current.get(task_id);
    if (ctl) {
      try { ctl.abort(); } catch { /* noop */ }
      abortersRef.current.delete(task_id);
    }
    removeUpload(task_id);
  };

  /**
   * Kick off an upload for one new task. Returns immediately with a
   * placeholder — actual upload work runs sequentially (chained
   * off `chainRef`) so a 968-file second task doesn't start hammering
   * R2 while the first is still going. Each task gets its own visible
   * "waiting" row until the chain reaches it.
   *
   * @param {Object} params { name, groupFiles: File[], portraitFiles: File[] }
   */
  const enqueueTask = ({ name, groupFiles, portraitFiles }) => {
    // Reserve a client-side placeholder id so the UI has *something*
    // to show while queued behind another task.
    const placeholderId = `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const total = groupFiles.length + portraitFiles.length;
    updateUpload(placeholderId, {
      name, status: "waiting", done: 0, total, failed: 0, placeholder: true,
    });

    const run = async () => {
      if (cancelledRef.current.has(placeholderId)) {
        removeUpload(placeholderId);
        return;
      }
      // Promote from "waiting" placeholder → the real upload.
      removeUpload(placeholderId);
      await _startTaskUpload({ name, groupFiles, portraitFiles });
    };

    // Chain onto the previous upload so only one runs at a time.
    // .catch keeps the chain alive even if a task errors out.
    chainRef.current = chainRef.current
      .catch(() => {})
      .then(run);
    return placeholderId;
  };

  const _startTaskUpload = async ({ name, groupFiles, portraitFiles }) => {
    // 1. Ask backend for R2 keys + presigned PUT urls
    let init;
    try {
      const { data } = await axios.post(`${API}/group-portraits/tasks/init`, {
        name,
        group_photos: groupFiles.map((f) => ({
          filename: f.name, content_type: f.type || "image/jpeg",
          // `webkitRelativePath` is "Day 1/IMG_001.jpg" when the user
          // picked a folder; empty string otherwise. Preserving it lets
          // the backend keep matched files inside their original
          // subfolder in the output ZIP.
          rel_path: f.webkitRelativePath || f.name,
        })),
        portrait_photos: portraitFiles.map((f) => ({
          filename: f.name, content_type: f.type || "image/jpeg",
          rel_path: f.webkitRelativePath || f.name,
        })),
      });
      init = data;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "init failed";
      toast.error(`Couldn't start task: ${msg}`);
      throw err;
    }

    const { task_id } = init;
    const groupPlan = init.upload_urls.group;
    const portraitPlan = init.upload_urls.portraits;
    const total = groupPlan.length + portraitPlan.length;

    updateUpload(task_id, {
      name: init.name, status: "uploading",
      done: 0, total, failed: 0,
    });
    toast.success(`Task queued — uploading ${total} file${total === 1 ? "" : "s"} in background`);

    // 2. PUT files with bounded concurrency + retry
    const CONC = 4;
    const jobs = [
      ...groupPlan.map((p, i) => ({ p, file: groupFiles[i], kind: "group" })),
      ...portraitPlan.map((p, i) => ({ p, file: portraitFiles[i], kind: "portraits" })),
    ];
    let cursor = 0, done = 0, failed = 0, lastPing = 0;

    const controller = new AbortController();
    abortersRef.current.set(task_id, controller);

    const workers = new Array(Math.min(CONC, jobs.length)).fill(null).map(async () => {
      while (cursor < jobs.length) {
        if (cancelledRef.current.has(task_id)) return;   // aborted by user
        const idx = cursor++;
        const { p, file } = jobs[idx];
        try {
          await retryingPut(p.upload_url, file, p.content_type, controller.signal);
          done++;
        } catch (err) {
          if (err?.name === "AbortError") return;
          failed++;
          console.warn("upload failed", p.filename, err);
        }
        if (cancelledRef.current.has(task_id)) return;
        updateUpload(task_id, { done, failed });
        // Throttle server progress pings.
        const now = Date.now();
        if (now - lastPing > PROGRESS_SAMPLE_MS || done + failed === total) {
          lastPing = now;
          axios.post(`${API}/group-portraits/tasks/${task_id}/upload-progress`, {
            done: done + failed, total,
          }).catch(() => {});
        }
      }
    });
    await Promise.all(workers);
    abortersRef.current.delete(task_id);

    if (cancelledRef.current.has(task_id)) {
      cancelledRef.current.delete(task_id);
      return task_id;    // silently: task was already deleted server-side
    }

    // 3. Finalize — server prunes any missing keys and marks queued
    try {
      await axios.post(`${API}/group-portraits/tasks/${task_id}/finalize`);
      updateUpload(task_id, { status: "done", done, failed });
      const msg = failed > 0
        ? `Upload finished with ${failed} failure${failed === 1 ? "" : "s"} — task queued`
        : `Upload complete — task queued`;
      toast.success(msg);
      setTimeout(() => removeUpload(task_id), 3000);
    } catch (err) {
      if (err.response?.status === 404) {
        // Task was deleted while we finalised — don't yell at the user.
        removeUpload(task_id);
      } else {
        const msg = err.response?.data?.detail || err.message || "finalize failed";
        updateUpload(task_id, { status: "failed", error: msg });
        toast.error(`Couldn't finalise task: ${msg}`);
      }
    }
    return task_id;
  };

  return (
    <UploadCtx.Provider value={{ uploads, enqueueTask, removeUpload, cancelUpload }}>
      {children}
    </UploadCtx.Provider>
  );
}

export function useGroupPortraitUploads() {
  const ctx = useContext(UploadCtx);
  if (!ctx) throw new Error("useGroupPortraitUploads outside provider");
  return ctx;
}
